import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputBase,
  Paper,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search,
  Phone,
  Facebook,
  Twitter,
} from '@mui/icons-material';

const ZarorHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null); // Start with null to prevent flash
  const [logoLoading, setLogoLoading] = useState(true);
  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  // Fetch logo from settings
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await axios.get(`${apiUrl}/settings`);
        const settings = response.data;
        
        if (settings.site_logo) {
          // Fetch logo media details
          try {
            const mediaResponse = await axios.get(`${apiUrl}/media`);
            const logoMedia = mediaResponse.data.find(m => m.id === parseInt(settings.site_logo));
            if (logoMedia) {
              const baseUrl = apiUrl.replace('/api', '');
              setLogoUrl(`${baseUrl}/api/uploads/${logoMedia.filename}`);
            } else {
              // No custom logo found, use default
              setLogoUrl('/logo192.png');
            }
          } catch (err) {
            console.error('Error fetching logo media:', err);
            setLogoUrl('/logo192.png');
          }
        } else {
          // No logo set in settings, use default
          setLogoUrl('/logo192.png');
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        // Use default logo on error
        setLogoUrl('/logo192.png');
      } finally {
        setLogoLoading(false);
      }
    };

    fetchLogo();
  }, [apiUrl]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const topBar = (
    <Box
      sx={{
        backgroundColor: '#f5f5f5',
        py: 1,
        borderBottom: '1px solid #e0e0e0',
        position: 'relative',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone sx={{ fontSize: 16, color: '#666' }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                0044 121 661 6527
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" sx={{ color: '#666' }} component="a" href="https://www.facebook.com/zarorsports" target="_blank" rel="noopener noreferrer">
              <Facebook sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton size="small" sx={{ color: '#666' }} component="a" href="https://x.com/zarorsportsltd" target="_blank" rel="noopener noreferrer">
              <Twitter sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Container>
    </Box>
  );

  const mobileMenu = (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={handleDrawerToggle}
      sx={{
        '& .MuiDrawer-paper': { 
          width: 280, 
          boxSizing: 'border-box',
          zIndex: (theme) => theme.zIndex.drawer,
        },
        zIndex: (theme) => theme.zIndex.drawer,
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Zaror Sports
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          <ListItem button component={Link} to="/" onClick={handleDrawerToggle}>
            <ListItemText primary="HOME" />
          </ListItem>
          <ListItem button component={Link} to="/about" onClick={handleDrawerToggle}>
            <ListItemText primary="ABOUT US" />
          </ListItem>
          <ListItem button component={Link} to="/contact" onClick={handleDrawerToggle}>
            <ListItemText primary="CONTACT US" />
          </ListItem>
          <ListItem button component={Link} to="/tracksuits" onClick={handleDrawerToggle}>
            <ListItemText primary="TRACKSUITS" />
          </ListItem>
          <ListItem button component={Link} to="/privacy-policy" onClick={handleDrawerToggle}>
            <ListItemText primary="PRIVACY POLICY" />
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );

  return (
    <>
      {topBar}
      <AppBar 
        position="sticky" 
        color="default" 
        elevation={0}
        sx={{ 
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ py: 1 }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 2, 
                  color: '#000000',
                  zIndex: (theme) => theme.zIndex.appBar + 1,
                  position: 'relative',
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Box sx={{ flexGrow: isMobile ? 0 : 1, display: 'flex', alignItems: 'center' }}>
              <Link to="/" style={{ textDecoration: 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {logoUrl && (
                    <img
                      src={logoUrl}
                      alt="Zaror Sports Logo"
                      style={{
                        height: '60px',
                        width: 'auto',
                        marginRight: '16px',
                        opacity: logoLoading ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                      }}
                      onError={(e) => {
                        // Fallback to default logo if image fails to load
                        e.target.src = '/logo192.png';
                      }}
                    />
                  )}
                </Box>
              </Link>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Button
                  component={Link}
                  to="/"
                  sx={{
                    color: '#000000',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#72EFD0',
                    },
                  }}
                >
                  HOME
                </Button>
                <Button
                  component={Link}
                  to="/about"
                  sx={{
                    color: '#000000',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#72EFD0',
                    },
                  }}
                >
                  ABOUT US
                </Button>
                <Button
                  component={Link}
                  to="/contact"
                  sx={{
                    color: '#000000',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#72EFD0',
                    },
                  }}
                >
                  CONTACT US
                </Button>
                <Button
                  component={Link}
                  to="/tracksuits"
                  sx={{
                    color: '#000000',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#72EFD0',
                    },
                  }}
                >
                  TRACKSUITS
                </Button>
                <Button
                  component={Link}
                  to="/privacy-policy"
                  sx={{
                    color: '#000000',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: '#72EFD0',
                    },
                  }}
                >
                  PRIVACY POLICY
                </Button>
              </Box>
            )}

            {/* Search */}
            {!isMobile && (
              <Box sx={{ ml: 4 }}>
                <Paper
                  component="form"
                  sx={{
                    p: '2px 4px',
                    display: 'flex',
                    alignItems: 'center',
                    width: 200,
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none',
                  }}
                >
                  <InputBase
                    sx={{ ml: 1, flex: 1 }}
                    placeholder="Search"
                    inputProps={{ 'aria-label': 'search' }}
                  />
                  <IconButton
                    type="submit"
                    sx={{ p: '10px' }}
                    aria-label="search"
                  >
                    <Search />
                  </IconButton>
                </Paper>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      {mobileMenu}
    </>
  );
};

export default ZarorHeader;

